/**
 * iter216 — Public customer tracking page for a TMS transport job.
 *
 * Route: /tms/track/:token   (UNAUTHENTICATED)
 * API:   GET /api/transport/track/{token}
 *
 * Shows a live map with the truck's current position, its trail,
 * the customer's delivery pin, and their queue position — polled
 * every 30s. Deliberately hides everything internal (vehicle rego,
 * driver name, other customers' names, any pricing).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Truck, MapPin, CheckCircle, Clock, Warning, ArrowClockwise } from '@phosphor-icons/react';
import { getPublicTracking } from '../lib/api';

// Small inline SVG icons so we don't ship binary marker assets.
const truckIcon = L.divIcon({
  className: '',
  html: `<div style="background:#0f172a;color:#fff;border-radius:999px;padding:6px;box-shadow:0 2px 8px rgba(0,0,0,.35);border:2px solid #fbbf24;display:inline-flex;align-items:center;justify-content:center;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
  </div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});
const homeIcon = L.divIcon({
  className: '',
  html: `<div style="background:#059669;color:#fff;border-radius:999px;padding:5px;box-shadow:0 2px 6px rgba(0,0,0,.3);"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});
const deliveredIcon = L.divIcon({
  className: '',
  html: `<div style="background:#10b981;color:#fff;border-radius:999px;width:14px;height:14px;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);"></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});
const pendingIcon = L.divIcon({
  className: '',
  html: `<div style="background:#94a3b8;color:#fff;border-radius:999px;width:12px;height:12px;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);"></div>`,
  iconSize: [12, 12], iconAnchor: [6, 6],
});

const STATUS_META = {
  new:        { label: 'Awaiting dispatch', color: 'text-slate-700 bg-slate-100 border-slate-300', Icon: Clock },
  planned:    { label: 'Scheduled for delivery', color: 'text-blue-700 bg-blue-50 border-blue-200', Icon: Clock },
  in_transit: { label: 'Out for delivery', color: 'text-amber-700 bg-amber-50 border-amber-200', Icon: Truck },
  delivered:  { label: 'Delivered', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle },
  cancelled:  { label: 'Cancelled', color: 'text-red-700 bg-red-50 border-red-200', Icon: Warning },
};

function fmtTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}
function fmtRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return `${h} h ago`;
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-slate-600 flex-wrap">
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-900 border-2 border-amber-400" />Truck</span>
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-600" />Your stop</span>
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow" />Delivered</span>
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-400 border-2 border-white shadow" />Upcoming</span>
    </div>
  );
}

// iter232d — Vertical multi-stop timeline for the public page. Shows each
// stop as a dot on a left-side spine; the viewer's own stop is expanded
// with full address + customer, every other stop is masked to its
// public status so no peer PII leaks.
function StopTimeline({ timeline }) {
  const me = timeline.find((s) => s.is_me);
  const total = timeline.length;
  const myPos = me ? me.sequence : null;

  const dotStyle = (status, isMe) => {
    if (isMe) return 'bg-emerald-600 ring-4 ring-emerald-100 border-emerald-600';
    if (status === 'completed') return 'bg-emerald-500 border-emerald-500';
    if (status === 'arrived') return 'bg-amber-500 border-amber-500';
    return 'bg-slate-300 border-slate-300';
  };

  const label = (s) => {
    if (s.status === 'completed') return 'Delivered';
    if (s.status === 'arrived') return 'Driver on-site';
    return 'Pending';
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="tms-track-timeline">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-sm font-semibold">
          {myPos != null ? `Stop ${myPos} of ${total}` : `Multi-stop run · ${total} stops`}
        </div>
        <div className="text-[11px] text-slate-500">
          Milk run · {timeline.filter((s) => s.status === 'completed').length} completed
        </div>
      </div>
      <ol className="relative border-l border-slate-200 ml-2 space-y-3">
        {timeline.map((s) => (
          <li key={s.sequence} className="pl-4 relative" data-testid={`tms-track-timeline-stop-${s.sequence}${s.is_me ? '-me' : ''}`}>
            <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 ${dotStyle(s.status, s.is_me)}`} />
            <div className={`flex items-start justify-between gap-2 ${s.is_me ? 'rounded-md bg-emerald-50 border border-emerald-200 p-2' : ''}`}>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-800 flex items-center gap-2">
                  Stop {s.sequence}
                  {s.is_me && (
                    <span className="text-[10px] px-1.5 py-[1px] bg-emerald-600 text-white rounded uppercase tracking-wide">
                      You
                    </span>
                  )}
                </div>
                {s.is_me ? (
                  <>
                    <div className="text-[12px] text-slate-700 truncate">{s.customer_name || '—'}</div>
                    <div className="text-[11px] text-slate-500 truncate">{s.address || '—'}</div>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500">
                    {s.suburb ? `${s.suburb} · ` : ''}{label(s)}
                  </div>
                )}
                {s.is_me && s.pod_captured_at && (
                  <div className="text-[11px] text-emerald-700 mt-0.5">
                    Delivered at {fmtTime(s.pod_captured_at)}
                  </div>
                )}
                {s.is_me && !s.pod_captured_at && s.arrived_at && (
                  <div className="text-[11px] text-amber-700 mt-0.5">
                    Driver arrived at {fmtTime(s.arrived_at)}
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${
                  s.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  s.status === 'arrived' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-slate-50 text-slate-600 border-slate-200'
                }`}>
                  {label(s)}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function PublicTmsTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const r = await getPublicTracking(token);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'not_found' : 'generic');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    // Poll every 30s so the customer sees the truck moving. Pause while
    // the tab is hidden so a customer who leaves the page open all day
    // doesn't hammer the endpoint.
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchData();
      setTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500">Loading tracking…</div>
      </div>
    );
  }
  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Warning size={40} className="text-amber-500 mx-auto mb-3" />
          <div className="text-sm font-semibold">Tracking link not found</div>
          <div className="text-xs text-slate-500 mt-1">This link may have expired or been revoked. Please contact the sender.</div>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-sm text-slate-500">Something went wrong. Try again in a moment.</div>
          <button className="mt-3 text-xs px-3 py-2 bg-slate-900 text-white rounded" onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[data.job.status] || STATUS_META.new;
  const StatusIcon = meta.Icon;

  // Compute map center — prefer truck, then delivery pin, then Australia default.
  const center = data.truck ? [data.truck.lat, data.truck.lon]
    : (data.job.lat && data.job.lon ? [data.job.lat, data.job.lon] : [-33.87, 151.21]);
  const zoom = data.truck || data.job.lat ? 12 : 5;
  const trail = (data.trail || []).map((p) => [p.lat, p.lon]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tms-public-tracking">
      <div className="bg-slate-900 text-white px-4 py-3 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Truck size={22} weight="fill" className="text-amber-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] opacity-70">Delivery tracking · {data.job.job_number}</div>
            <div className="text-sm font-semibold truncate" data-testid="tms-track-heading">
              {data.job.customer_name || 'Your delivery'}
            </div>
          </div>
          <button onClick={fetchData} className="text-[11px] px-2 py-1 rounded bg-slate-800 border border-slate-700 flex items-center gap-1" data-testid="tms-track-refresh">
            <ArrowClockwise size={12} /> Refresh
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <div className={`rounded-lg border p-3 flex items-center gap-3 ${meta.color}`} data-testid="tms-track-status">
          <StatusIcon size={22} weight="fill" />
          <div className="flex-1">
            <div className="text-sm font-semibold">{meta.label}</div>
            <div className="text-[11px] opacity-80">
              {data.job.status === 'delivered' && data.job.delivered_at && `Delivered at ${fmtTime(data.job.delivered_at)}`}
              {data.job.status === 'in_transit' && data.job.arrival_eta && `ETA ${fmtTime(data.job.arrival_eta)}${data.job.live_eta_is_fresh ? ' · live' : ''}`}
              {data.job.status === 'planned' && data.job.planned_date && `Scheduled for ${data.job.planned_date}`}
              {data.job.status === 'new' && 'Your driver has not been dispatched yet'}
            </div>
            {data.job.window_breach === 'late' && (
              <div className="text-[11px] mt-1 font-semibold text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="tms-track-breach">
                <Warning size={11} weight="fill" className="inline mr-1" />
                Running late — after your promised window {data.job.window_start ? `${data.job.window_start}–${data.job.window_end || ''}` : ''}. Sorry for the delay.
              </div>
            )}
          </div>
        </div>

        {data.job.status === 'in_transit' && (
          <div className="bg-white rounded-lg border border-slate-200 p-3 grid grid-cols-3 gap-2 text-center" data-testid="tms-track-queue">
            <div>
              <div className="text-lg font-bold tabular-nums">{data.queue.stops_ahead_pending}</div>
              <div className="text-[10px] text-slate-500 uppercase">stops before you</div>
            </div>
            <div>
              <div className="text-lg font-bold tabular-nums text-emerald-600">{data.queue.stops_ahead_delivered}</div>
              <div className="text-[10px] text-slate-500 uppercase">already delivered</div>
            </div>
            <div>
              <div className="text-lg font-bold tabular-nums">{data.queue.position || '—'}</div>
              <div className="text-[10px] text-slate-500 uppercase">your position</div>
            </div>
          </div>
        )}

        {data.multi_stop && Array.isArray(data.stop_timeline) && data.stop_timeline.length > 0 && (
          <StopTimeline timeline={data.stop_timeline} />
        )}

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="h-[380px]">
            <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {trail.length > 1 && (
                <Polyline positions={trail} pathOptions={{ color: '#f59e0b', weight: 3, opacity: 0.7 }} />
              )}
              {(data.peers || []).filter((p) => !p.is_me && p.lat && p.lon).map((p, i) => (
                <Marker key={`peer-${i}`} position={[p.lat, p.lon]}
                        icon={p.status === 'delivered' ? deliveredIcon : pendingIcon}>
                  <Popup>Stop #{p.sequence_no || '—'} · {p.status}</Popup>
                </Marker>
              ))}
              {data.job.lat && data.job.lon && (
                <Marker position={[data.job.lat, data.job.lon]} icon={homeIcon}>
                  <Popup>Your delivery · {data.job.delivery_location}</Popup>
                </Marker>
              )}
              {data.truck && (
                <Marker position={[data.truck.lat, data.truck.lon]} icon={truckIcon}>
                  <Popup>Truck · updated {fmtRelative(data.truck.at)}</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
          <div className="p-2 border-t border-slate-200 flex items-center justify-between gap-2 flex-wrap">
            <Legend />
            <div className="text-[11px] text-slate-500 tabular-nums" data-testid="tms-track-last-ping">
              {data.truck?.at
                ? `Truck last seen ${fmtRelative(data.truck.at)}`
                : (data.job.status === 'in_transit' ? 'Waiting for first GPS ping…' : 'No live signal yet')}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3 text-sm" data-testid="tms-track-address">
          <div className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin size={12} /> Delivering to</div>
          <div className="font-medium">{data.job.delivery_location || '—'}</div>
        </div>

        <div className="text-[10px] text-center text-slate-400 pt-1 pb-6">
          This tracking page refreshes automatically every 30 seconds. Powered by Stockaris Transport.
        </div>
      </div>
    </div>
  );
}
